import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, from, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Branch } from './branch.model';
import { Page } from '../../models/page-wrapper.model';
import { environment } from '../../../environments/environment';
import { PickerRecentCacheService } from '../../shared/services/picker-recent-cache.service';
import { EntityDropdownPickerAdapter } from '../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { PickerRecentRecord } from '../../shared/components/entity-dropdown-picker/picker-recent.model';

@Injectable({
  providedIn: 'root'
})
export class BranchesService {
  private apiUrl = `${environment.restApiUrl}masterdata/branches`;
  private readonly pickerEntityKey = 'branch';

  constructor(
    private http: HttpClient,
    private recentPickerCache: PickerRecentCacheService
  ) { }

  // Get all branches with pagination
  getAllBranches(params?: any): Observable<Page<Branch>> {
    return this.http.get<Page<Branch>>(this.apiUrl, { params: params }).pipe(
      catchError(this.handleError)
    );
  }

  // Get branch by ID
  getBranchById(id: number): Observable<Branch> {
    return this.http.get<Branch>(`${this.apiUrl}/${id}`).pipe(
      catchError(this.handleError)
    );
  }

  searchBranches(query: string, page = 0, size = 25): Observable<Page<Branch>> {
    return forkJoin([
      this.searchBranchesByField('name', query, page, size),
      this.searchBranchesByField('code', query, page, size),
    ]).pipe(
      map(([namePage, codePage]) => this.mergeBranchPages(namePage, codePage, page, size)),
      catchError(this.handleError)
    );
  }

  loadRecentBranches(limit = 10): Observable<Branch[]> {
    return from(this.recentPickerCache.loadRecent<Branch>(this.pickerEntityKey, limit));
  }

  getFreshBranchById(id: number | string): Observable<Branch> {
    return this.getBranchById(Number(id));
  }

  rememberBranch(branch: Branch): Observable<void> {
    const record: PickerRecentRecord<Branch> = {
      key: `${this.pickerEntityKey}::${branch.id}`,
      entityKey: this.pickerEntityKey,
      userKey: this.getCurrentUserKey(),
      entityId: branch.id,
      label: `${branch.name ?? ''}${branch.code ? ` - ${branch.code}` : ''}`.trim(),
      payload: branch,
      lastUsedAt: Date.now(),
    };

    return from(this.recentPickerCache.remember(record));
  }

  createBranchPickerAdapter(): EntityDropdownPickerAdapter<Branch> {
    return {
      entityKey: this.pickerEntityKey,
      loadRecent: (limit?: number) => this.loadRecentBranches(limit),
      search: (query: string, page: number, size: number) => this.searchBranches(query, page, size),
      getFreshById: (id: number | string) => this.getFreshBranchById(id),
      rememberSelection: (branch: Branch) => this.rememberBranch(branch),
      getId: (branch: Branch) => branch.id,
      getLabel: (branch: Branch) => `${branch.name ?? branch.code ?? branch.id ?? ''}${branch.code && branch.name ? ` - ${branch.code}` : ''}`.trim(),
      getSecondaryLabel: () => '',
      filterBy: 'name,code',
    };
  }

  // Create new branch
  createBranch(branch: Branch): Observable<Branch> {
    return this.http.post<Branch>(this.apiUrl, branch).pipe(
      catchError(this.handleError)
    );
  }

  // Update existing branch
  updateBranch(branch: Branch): Observable<Branch> {
    return this.http.put<Branch>(`${this.apiUrl}/${branch.id}`, branch).pipe(
      catchError(this.handleError)
    );
  }

  // Delete branch
  deleteBranch(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      catchError(this.handleError)
    );
  }

  private handleError(error: any): Observable<never> {
    console.error('An error occurred in BranchesService:', error);
    return throwError(() => new Error('Something bad happened; please try again later.'));
  }

  private getCurrentUserKey(): string {
    try {
      const rawUser = sessionStorage.getItem('appUser');
      if (!rawUser) {
        return 'anonymous';
      }

      const parsed = JSON.parse(rawUser);
      return String(parsed?.id ?? 'anonymous');
    } catch {
      return 'anonymous';
    }
  }

  private searchBranchesByField(
    field: 'name' | 'code',
    query: string,
    page: number,
    size: number
  ): Observable<Page<Branch>> {
    const params = {
      [field]: query,
      page,
      size,
    };

    return this.http.get<Page<Branch>>(this.apiUrl, { params }).pipe(
      catchError(() => of(this.emptyBranchPage(page, size)))
    );
  }

  private mergeBranchPages(
    firstPage: Page<Branch>,
    secondPage: Page<Branch>,
    page: number,
    size: number
  ): Page<Branch> {
    const merged = new Map<number, Branch>();
    [...(firstPage.content || []), ...(secondPage.content || [])].forEach((branch) => {
      if (branch?.id !== undefined && branch?.id !== null) {
        merged.set(branch.id, branch);
      }
    });

    const content = Array.from(merged.values()).slice(0, size);

    return {
      ...firstPage,
      content,
      number: page,
      size,
      totalElements: merged.size,
      numberOfElements: content.length,
    };
  }

  private emptyBranchPage(page: number, size: number): Page<Branch> {
    return {
      content: [],
      pageable: {
        pageNumber: page,
        pageSize: size,
        sort: { sorted: false, unsorted: true, empty: true },
        offset: page * size,
        paged: true,
        unpaged: false,
      },
      last: true,
      totalPages: 0,
      totalElements: 0,
      size,
      number: page,
      sort: { sorted: false, unsorted: true, empty: true },
      first: page === 0,
      numberOfElements: 0,
      empty: true,
    };
  }
}
